import { useLayoutEffect, useState } from 'react'
import { ScreenNotInDesign } from '../designs/not-in-design'
import { DESIGNS } from '../designs/registry'
import type { DesignScreens } from '../designs/types'
import { PreviewDataProvider } from '../features/preview-data'
import { getActiveVariant, setActiveVariant, VARIANTS, type VariantId } from '../lib/variants'

const readActive = () => getActiveVariant(typeof window !== 'undefined' ? window.location.search : '')

/**
 * Public, no-auth preview of every design language on mock data. Sits outside the Clerk-protected
 * tree. Screens are discovered from `Object.keys(design.screens)` so new screens appear automatically.
 */
export function PreviewRoot() {
  const [active, setActive] = useState<VariantId>(readActive)
  const [screenKey, setScreenKey] = useState<string>('locations')

  useLayoutEffect(() => {
    document.documentElement.dataset.design = active
  }, [active])

  const design = DESIGNS[active]
  const keys = Object.keys(design.screens) as (keyof DesignScreens)[]
  const current = keys.includes(screenKey as keyof DesignScreens) ? (screenKey as keyof DesignScreens) : keys[0]
  const Screen = design.screens[current] ?? (() => <ScreenNotInDesign name={current} />)

  return (
    <PreviewDataProvider>
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-3 border-b border-neutral-500/40 bg-neutral-900 px-3 py-1 text-xs text-neutral-200">
          <span className="rounded bg-amber-400 px-2 py-0.5 font-semibold tracking-wide text-neutral-900">
            PREVIEW — mock data
          </span>
          <label>
            Design{' '}
            <select
              value={active}
              onChange={(e) => {
                setActiveVariant(e.target.value)
                setActive(e.target.value as VariantId)
              }}
              className="rounded bg-neutral-800 px-1 py-0.5"
            >
              {VARIANTS.map((v) => (
                <option key={v} value={v}>
                  {DESIGNS[v].name}
                </option>
              ))}
            </select>
          </label>
          <nav className="flex gap-1">
            {keys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setScreenKey(k)}
                className={`rounded px-2 py-0.5 ${k === current ? 'bg-neutral-600' : 'bg-neutral-800'}`}
              >
                {k}
              </button>
            ))}
          </nav>
        </div>
        <div className="min-h-0 flex-1">
          <design.Shell>
            <Screen />
          </design.Shell>
        </div>
      </div>
    </PreviewDataProvider>
  )
}
